import { useEffect } from 'react';
import './WaveCrashAnimation.css';

/**
 * PROFESSIONAL WAVE CRASH ANIMATION
 * Water-type physical move - cloaks self in water and slams opponent
 * 
 * Key Features:
 * - Water-type physical attack (120 base power, 100% accuracy)
 * - User takes 33% recoil damage
 * - Water-cloaked body slam with massive splash impact
 * - Professional gaming quality with aquatic charge and recoil
 */
export default function WaveCrashAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    const container = playerEl.closest('.battle-screen') || playerEl.parentElement || document.body;
    const containerRect = container.getBoundingClientRect();

    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();

    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    const dx = opponentX - playerX;
    const dy = opponentY - playerY;
    const distance = Math.hypot(dx, dy);

    const elements = [];

    // PHASE 1: Water Cloak Buildup (0-500ms)
    playerEl.classList.add('wave-crash-user-charge');

    const waterCloak = document.createElement('div');
    waterCloak.className = 'wave-crash-water-cloak';
    waterCloak.style.left = `${playerX - 60}px`;
    waterCloak.style.top = `${playerY - 60}px`;
    container.appendChild(waterCloak);
    elements.push(waterCloak);

    // Swirling water particles around player
    for (let i = 0; i < 14; i++) {
      const swirl = document.createElement('div');
      swirl.className = 'wave-crash-swirl-particle';
      const angle = (i / 14) * 360;
      const radius = 50 + (i % 3) * 10;
      swirl.style.left = `${playerX + Math.cos(angle * Math.PI / 180) * radius - 4}px`;
      swirl.style.top = `${playerY + Math.sin(angle * Math.PI / 180) * radius - 4}px`;
      swirl.style.animationDelay = `${i * 30}ms`;
      container.appendChild(swirl);
      elements.push(swirl);
    }

    // PHASE 2: Aquatic Charge (500-900ms)
    const phase2Timer = setTimeout(() => {
      playerEl.classList.add('wave-crash-user-dash');
      playerEl.style.setProperty('--dash-x', `${dx * 0.85}px`);
      playerEl.style.setProperty('--dash-y', `${dy * 0.85}px`);

      // Water wave trail during charge
      const waveTrail = document.createElement('div');
      waveTrail.className = 'wave-crash-wave-trail';
      waveTrail.style.left = `${playerX - 15}px`;
      waveTrail.style.top = `${playerY - 15}px`;
      waveTrail.style.width = `${distance * 0.85}px`;
      waveTrail.style.transform = `rotate(${Math.atan2(dy, dx) * 180 / Math.PI}deg)`;
      waveTrail.style.transformOrigin = 'left center';
      container.appendChild(waveTrail);
      elements.push(waveTrail);

      // Water droplets during charge
      for (let i = 0; i < 16; i++) {
        const dropTimer = setTimeout(() => {
          const drop = document.createElement('div');
          drop.className = 'wave-crash-charge-droplet';
          const progress = i / 16;
          drop.style.left = `${playerX + dx * progress * 0.85 + (Math.random() - 0.5) * 30 - 4}px`;
          drop.style.top = `${playerY + dy * progress * 0.85 + (Math.random() - 0.5) * 30 - 4}px`;
          container.appendChild(drop);
          elements.push(drop);
        }, i * 22);
        elements.push({ timer: dropTimer, type: 'timer' });
      }
    }, 500);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Crashing Impact (900-1400ms)
    const phase3Timer = setTimeout(() => {
      opponentEl.classList.add('wave-crash-opponent-hit');
      playerEl.classList.remove('wave-crash-user-dash');

      // Massive water crash explosion
      const crashImpact = document.createElement('div');
      crashImpact.className = 'wave-crash-impact';
      crashImpact.style.left = `${opponentX - 110}px`;
      crashImpact.style.top = `${opponentY - 110}px`;
      container.appendChild(crashImpact);
      elements.push(crashImpact);

      // Water splash shockwaves
      for (let i = 0; i < 3; i++) {
        const swTimer = setTimeout(() => {
          const shockwave = document.createElement('div');
          shockwave.className = 'wave-crash-shockwave';
          shockwave.style.left = `${opponentX - 80}px`;
          shockwave.style.top = `${opponentY - 80}px`;
          container.appendChild(shockwave);
          elements.push(shockwave);
        }, i * 100);
        elements.push({ timer: swTimer, type: 'timer' });
      }

      // Splash particles in all directions
      for (let i = 0; i < 22; i++) {
        const splashAngle = (i / 22) * 360;
        const splashDist = 60 + Math.random() * 50;
        const splash = document.createElement('div');
        splash.className = 'wave-crash-splash-particle';
        splash.style.left = `${opponentX - 4}px`;
        splash.style.top = `${opponentY - 4}px`;
        splash.style.setProperty('--scatter-x', `${Math.cos(splashAngle * Math.PI / 180) * splashDist}px`);
        splash.style.setProperty('--scatter-y', `${Math.sin(splashAngle * Math.PI / 180) * splashDist}px`);
        splash.style.animationDelay = `${i * 12}ms`;
        container.appendChild(splash);
        elements.push(splash);
      }

      // Water column burst upward
      const column = document.createElement('div');
      column.className = 'wave-crash-water-column';
      column.style.left = `${opponentX - 25}px`;
      column.style.top = `${opponentY - 80}px`;
      container.appendChild(column);
      elements.push(column);
    }, 900);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // PHASE 4: Recoil Effect (1400-1800ms)
    const phase4Timer = setTimeout(() => {
      playerEl.classList.remove('wave-crash-user-charge');
      playerEl.classList.add('wave-crash-user-recoil');

      // Recoil splash on player
      const recoilSplash = document.createElement('div');
      recoilSplash.className = 'wave-crash-recoil-splash';
      recoilSplash.style.left = `${playerX - 40}px`;
      recoilSplash.style.top = `${playerY - 40}px`;
      container.appendChild(recoilSplash);
      elements.push(recoilSplash);

      for (let i = 0; i < 6; i++) {
        const recoilDrop = document.createElement('div');
        recoilDrop.className = 'wave-crash-recoil-droplet';
        const angle = (i / 6) * 360;
        const dist = 25 + Math.random() * 15;
        recoilDrop.style.left = `${playerX - 3}px`;
        recoilDrop.style.top = `${playerY - 3}px`;
        recoilDrop.style.setProperty('--scatter-x', `${Math.cos(angle * Math.PI / 180) * dist}px`);
        recoilDrop.style.setProperty('--scatter-y', `${Math.sin(angle * Math.PI / 180) * dist}px`);
        container.appendChild(recoilDrop);
        elements.push(recoilDrop);
      }
    }, 1400);
    elements.push({ timer: phase4Timer, type: 'timer' });

    // CLEANUP (1800ms)
    const cleanupTimer = setTimeout(() => {
      playerEl.classList.remove('wave-crash-user-charge', 'wave-crash-user-dash', 'wave-crash-user-recoil');
      opponentEl.classList.remove('wave-crash-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
      onComplete();
    }, 1800);

    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('wave-crash-user-charge', 'wave-crash-user-dash', 'wave-crash-user-recoil');
      opponentEl.classList.remove('wave-crash-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

WaveCrashAnimation.moveType = 'water';
